/* eslint-disable max-len */
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { Colors } from './Colors';

const fontFamilies = ['Inter', 'sans-serif'];

let Theme = createTheme({
  mixins: {
    toolbar: {
      minHeight: 56,
      '@media (min-width:0px) and (orientation: landscape)': {
        minHeight: 48,
      },
      '@media (min-width:600px)': {
        minHeight: 64,
      },
    },
  },
  palette: {
    mode: 'light', // Updated from 'type' to 'mode' as per MUI v5+
    common: {
      black: '#2e384d',
      white: '#fff',
    },
    primary: {
      light: '#6b8aff',
      main: '#155eef',
      dark: '#0035cb',
      contrastText: '#ffffff',
    },
    secondary: {
      light: '#b7c8df',
      main: '#8797ad',
      dark: '#59697e',
      contrastText: '#000000',
    },
    info: {
      light: '#62e3ac',
      main: '#22b07d',
      dark: '#007f51',
      contrastText: '#000000',
    },
    warning: {
      light: '#ffb844',
      main: '#ff8700',
      dark: '#c55800',
      contrastText: '#000000',
    },
    error: {
      light: '#ff5533',
      main: '#e80000',
      dark: '#ad0000',
      contrastText: '#000000',
    },
    success: {
      light: '#6dde5e',
      main: '#33ab2e',
      dark: '#007b00',
      contrastText: '#000000',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: '#2e384d',
      secondary: '#b0bac9',
      disabled: 'rgba(0, 0, 0, 0.38)',
      hint: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    background: {
      paper: '#fff',
      default: Colors.navigatorBgColor,
    },
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.08)',
      hoverOpacity: 0.08,
      selected: 'rgba(0, 0, 0, 0.14)',
      selectedOpacity: 0.08,
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      disabledOpacity: 0.38,
      focus: 'rgba(0, 0, 0, 0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },
  shadows: Array(25).fill('none'), // Simplified shadows array
  typography: {
    fontFamily: fontFamilies.join(','),
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    h1: {
      fontFamily: ['font-black'].join(','),
      fontWeight: 300,
      fontSize: '6rem',
      lineHeight: 1.167,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontFamily: ['Inter-Bold'].join(','),
      fontWeight: 300,
      fontSize: '3.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontFamily: ['Inter-SemiBold'].join(','),
      fontWeight: 400,
      fontSize: '3rem',
      lineHeight: 1.167,
      letterSpacing: '0em',
    },
    h4: {
      fontFamily: ['Inter-Regular'].join(','),
      fontWeight: 400,
      fontSize: '2.125rem',
      lineHeight: 1.235,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontFamily: ['Inter-Regular'].join(','),
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.334,
      letterSpacing: '0em',
    },
    h6: {
      fontFamily: ['Inter-Light'].join(','),
      fontWeight: 500,
      fontSize: '0.9rem',
      lineHeight: 1.3,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontFamily: ['Inter-Bold'].join(','),
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontFamily: ['Inter-SemiBold'].join(','),
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontFamily: fontFamilies.join(','),
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontFamily: fontFamilies.join(','),
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      fontFamily: '"Inter", sans-serif',
      lineHeight: '1.375em',
      color: 'rgba(0, 0, 0, 0.54)',
    },
    overline: {
      fontFamily: fontFamilies.join(','),
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: '0.875rem',
      textTransform: 'capitalize',
      fontWeight: 500,
      fontFamily: fontFamilies.join(','),
    },
  },
  spacing: 10,
  shape: { borderRadius: 4 },
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          height: 49,
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          marginBottom: 16,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#f7faff',
          '& fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: '#E7EDFE',
          },
          '&$disabled': {
            '& fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: '#E7EDFE',
            },
          },
          '&:hover': {
            '& fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: '#E7EDFE',
            },
          },
        },
        input: {
          height: '1.5em',
        },
      },
    },
  },
});

Theme = responsiveFontSizes(Theme);
export default Theme;
